<template>
  <div class="sc-user-input-wrapper" :style="{fill: colors.userInput.text}">
    <suggestions v-if="chat.suggestions.length > 0"/>
    
    <div
      v-if="chat.file || uploading"
      class="file-container"
      :style="{
        backgroundColor: colors.userInput.text,
        color: colors.userInput.bg
      }"
    >
      <span class="icon-file-message">
        <svg
          :fill="colors.userInput.bg"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          class="sc-user-input--file-icon"
          x='0px'
          y='0px'
          viewBox='0 0 32 32'
        >
          <path d="M20.807 10.22l-2.030-2.029-10.15 10.148c-1.682 1.681-1.682 4.408 0 6.089s4.408 1.681 6.090 0l12.18-12.178c2.804-2.802 2.804-7.346 0-10.148-2.802-2.803-7.347-2.803-10.149 0l-12.788 12.787c-0.009 0.009-0.019 0.018-0.027 0.026-3.909 3.909-3.909 10.245 0 14.153 3.908 3.908 10.246 3.908 14.156 0 0.009-0.009 0.016-0.018 0.026-0.027l0.001 0.001 8.729-8.728-2.031-2.029-8.729 8.727c-0.009 0.008-0.018 0.018-0.026 0.026-2.784 2.783-7.312 2.783-10.096 0-2.783-2.783-2.783-7.31 0-10.093 0.010-0.009 0.019-0.018 0.028-0.026l-0.001-0.002 12.79-12.786c1.678-1.679 4.411-1.679 6.090 0s1.678 4.411 0 6.089l-12.18 12.178c-0.56 0.56-1.47 0.56-2.030 0-0.559-0.559-0.559-1.47 0-2.029l10.15-10.149z"></path>
        </svg>
      </span>
      <span v-if="fileError">
        {{ fileError }}
      </span>
      <span v-if="uploading">
        {{ $t('loading') }}
      </span>
      <span v-if="chat.file && chat.file.name && !fileError">
        {{ chat.file.name.length > 23 ? `${chat.file.name.substring(0, 23)}...` : chat.file.name }}
      </span>
      <span class="delete-file-message" @click="cancelFile">
        <img
          :src="icons.closeIcon"
          alt="close"
          height="10"
          :title="$t('Remove')"
        />
      </span>
    </div>
    <form
      class="sc-user-input"
      :class="{ active: inputActive }"
      :style="{ background: colors.userInput.bg }"
    >
      <menu-icon
        class="sc-user-menu"
        v-if="settings.menu.length > 0"
      />
      <div
        role="button"
        tabindex="0"
        @focus="setInputActive(true)"
        @blur="setInputActive(false)"
        @keydown="handleKey"
        @input="inputed"
        contenteditable="true"
        spellcheck="true"
        aria-autocomplete="list"
        :placeholder="settings.placeholder"
        class="sc-user-input--text"
        ref="userInput"
        :style="{ color: colors.userInput.text }"
      />
      <div class="sc-user-input--buttons">
        <div v-if="settings.showEmoji" class="sc-user-input--button">
          <emoji-icon/>
        </div>
        <div v-if="settings.showLocation" class="sc-user-input--button">
          <location-icon/>
        </div>
        <div v-if="settings.showFile" class="sc-user-input--button">
          <file-icon/>
        </div>
        <div class="sc-user-input--button">
          <send-icon @click.native.prevent="send"/>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import MenuIcon from './icons/MenuIcon.vue'
import EmojiIcon from './icons/EmojiIcon.vue'
import LocationIcon from './icons/LocationIcon.vue'
import FileIcon from './icons/FileIcon.vue'
import SendIcon from './icons/SendIcon.vue'
import Suggestions from './Suggestions.vue'

export default {
  name: 'UserInput',
  inject: {
    chat: {
      type: Object
    },
    widget: {
      type: Object
    },
    settings: {
      type: Object
    },
    colors: {
      type: Object
    },
    icons: {
      type: Object
    }
  },
  components: {
    MenuIcon,
    EmojiIcon,
    LocationIcon,
    FileIcon,
    SendIcon,
    Suggestions
  },
  data() {
    return {
      fileError: null,
      file: null,
      inputActive: false
    }
  },
  watch: {
    'chat.file' (newFile, oldFile) {
      if(newFile !== oldFile) {
        this.fileError = null
      }
    },
    'chat.message' (newMessage) {
      if (newMessage !== this.$refs.userInput.innerHTML) {
        this.$refs.userInput.innerHTML = newMessage
      }
    },
    'widget.isOpen' (isOpen) {
      isOpen && this.settings.focusOnOpen && this.focusUserInput()
    }
  },
  methods: {
    cancelFile() {
      this.chat.file = null
      this.fileError = null
    },
    setInputActive(onoff) {
      this.inputActive = onoff
    },
    inputed(event) {
      this.chat.message = event.target.innerText || event.target.textContent // event.target.innerHTML
    },
    send(event, source = 'send-button') {
      if (this.chat.message) {
        this.chat.send = {
          event,
          source,
          data: {
            type: "text",
            data: { text: this.chat.message }
          }
        }
        this.settings.autoFlush && (this.chat.message = '');
      }
      if (this.chat.file) {
        this.fileError = null
        const typeCheck = this.settings.allowedUploadTypes.indexOf(this.chat.file.type) > -1
        if (!typeCheck) {
          this.fileError = this.$t('unsupported_type')
        } else if ( this.chat.file.size > this.settings.allowedUploadSize ) {
          this.fileError = this.$t('unsupported_size')
        } else {
          this.chat.send = {
            event,
            source,
            data: {
              type: "file",
              data: {
                name: this.chat.file.name,
                size: this.chat.file.size,
                type: this.chat.file.type,
                file: this.chat.file
              }
            }
          }
          this.settings.autoFlush && (this.chat.file = null);
        }
      }
    },
    handleKey(event) {
      if (event.keyCode === 13 && !event.shiftKey) {
        this.send(event, 'enter-key')
        event.preventDefault()
      }
    },
    focusUserInput() {
      this.$nextTick(() => {
        this.$refs.userInput.focus()
      })
    },
  },
  mounted() {
    this.$refs.userInput.innerHTML = this.chat.message || ''
    this.widget.isOpen && this.settings.focusOnOpen && this.focusUserInput()
  },
  computed: {
    uploading() {
      return this.chat.messagingState === 2
    }
  }
}
</script>

<style>
.sc-user-input {
  min-height: 55px;
  margin: 0px;
  position: relative;
  bottom: 0;
  display: flex;
  background-color: #f4f7f9;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.sc-user-input--text {
  width: 200px;
  resize: none;
  border: none;
  outline: none;
  border-bottom-left-radius: 10px;
  box-sizing: border-box;
  padding: 18px 8px;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.33;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #565867;
  -webkit-font-smoothing: antialiased;
  max-height: 200px;
  overflow: scroll;
  bottom: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.sc-user-input--text:empty:before {
  content: attr(placeholder);
  display: block; /* For Firefox */
  color: rgba(86, 88, 103, 0.5);
  filter: contrast(15%);
  outline: none;
  cursor: text;
}

.sc-user-input--text[contenteditable=true]:focus:empty:before { position: absolute; }

.sc-user-input--text[contenteditable=true]:focus:empty:after { content: '\00a0'; }

.sc-user-input--buttons {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
}

.sc-user-input--button {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.sc-user-input--button:not(:last-child) {
  margin-right: 2px;
}

.sc-user-input.active {
  box-shadow: none;
  background-color: white;
  box-shadow: 0px -5px 20px 0px rgba(150, 165, 190, 0.2);
}

.sc-user-input--button label {
  position: relative;
  height: 24px;
  padding-left: 3px;
  cursor: pointer;
}

.sc-user-input--button label:hover path {
  fill: rgba(86, 88, 103, 1);
}

.sc-user-input--button input {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 99999;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  overflow: hidden;
}

.file-container {
  background-color: #f4f7f9;
  padding: 5px 20px;
  color: #565867;
}

.sc-user-input--file-icon {
  vertical-align: bottom;
  width: 16px;
  height: 16px;
}

.delete-file-message {
  font-style: normal;
  float: right;
  cursor: pointer;
  color: #c8cad0;
}

.delete-file-message:hover {
  color: #5d5e6d;
}

.icon-file-message {
  margin-right: 5px;
}
</style>
